import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import UiButton from './ui/UiButton'

/**
 * Shared detail template for individual and gift subscription views.
 *
 * Props:
 * - subscription: the subscription/genre object
 * - mode: 'recurring' | 'gift'
 * - onRecurringSelect: legacy callback for the "WYBIERZ" button (used when bundle props are not provided)
 * - onChoose: callback for the first "WYBIERZ" click when using the bundle store (adds tier and goes to checkout)
 * - onAddToBundle: callback for "Dodaj do subskrypcji (+1)" when tier is already in bundle
 * - onGoToCheckout: callback for "Przejdź do kasy" shown when tier is already in bundle
 * - inBundle: whether this tier is already in the bundle
 * - bundleQuantity: current quantity of this tier in the bundle
 * - maxReached: whether the bundle is at the 10-item cap
 * - showPoster: whether to render the poster image (default true)
 *
 * Note: Gift checkout is disabled for v1.0; mode='gift' shows a coming-soon message.
 */
export default function SubscriptionDetailTemplate({
  subscription,
  mode = 'recurring',
  onRecurringSelect,
  onChoose,
  onAddToBundle,
  onGoToCheckout,
  inBundle = false,
  bundleQuantity = 0,
  maxReached = false,
  showPoster = true,
}) {
  const { t } = useTranslation()

  if (!subscription) return null

  const features = subscription.features || []

  return (
    <div
      className={`grid gap-6 md:gap-10 items-center ${
        showPoster ? 'md:grid-cols-2' : 'grid-cols-1'
      }`}
    >
      {showPoster && (
        <div className="flex items-center justify-center">
          <img
            src={subscription.image}
            alt=""
            draggable={false}
            className="w-full max-w-[280px] sm:max-w-xs md:max-w-sm lg:max-w-md max-h-[50vh] md:max-h-[60vh] h-auto object-contain drop-shadow-2xl"
          />
        </div>
      )}

      <div className="flex flex-col justify-center">
        <p className="text-brand-orange font-bold tracking-wide uppercase text-sm mb-2">
          {subscription.subtitle}
        </p>
        <h2 className="font-climate text-2xl md:text-4xl text-brand-blue mb-4">
          {subscription.title}
        </h2>
        <p className="text-gray-700 mb-6 leading-relaxed">
          {subscription.description}
        </p>

        <ul className="space-y-3 mb-6 text-gray-800">
          {features
            .flatMap((feature) =>
              typeof feature === 'string' && feature.includes('|')
                ? feature.split('|').map((part) => part.trim()).filter(Boolean)
                : [feature]
            )
            .map((feature, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <Check
                  size={20}
                  className="shrink-0 mt-0.5 text-brand-gold"
                  aria-hidden="true"
                />
                <span>{feature}</span>
              </li>
            ))}
        </ul>

        {mode === 'recurring' ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="text-brand-blue">
              <div>
                <span className="text-3xl font-bold">{subscription.priceMonthly}</span>
                <span className="text-lg"> zł / {t('subscription.month')}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {t('subscription.shippingNote', { price: 15, defaultValue: '+15 zł wysyłka' })}
              </p>
              {inBundle && bundleQuantity > 0 && (
                <p className="text-sm text-brand-orange font-semibold mt-1">
                  {t('subscription.inBundle', 'W subskrypcji: {{quantity}}', { quantity: bundleQuantity })}
                </p>
              )}
            </div>
            <div className="flex flex-col items-start gap-2">
              {inBundle ? (
                <div className="flex flex-col gap-2">
                  <UiButton
                    type="button"
                    variant="gold"
                    size="lg"
                    onClick={onAddToBundle}
                    disabled={maxReached}
                  >
                    {t('subscription.addToBundle', 'Dodaj do subskrypcji (+1)')}
                  </UiButton>
                  <UiButton
                    type="button"
                    variant="secondary"
                    size="lg"
                    onClick={onGoToCheckout}
                  >
                    {t('subscription.goToCheckout', 'Przejdź do kasy')}
                  </UiButton>
                </div>
              ) : (
                <UiButton
                  type="button"
                  variant="gold"
                  size="lg"
                  onClick={onChoose || onRecurringSelect}
                  disabled={maxReached}
                >
                  {t('subscription.choose')}
                </UiButton>
              )}
              {maxReached && (
                <p className="text-xs text-brand-orange font-semibold max-w-[200px]">
                  {t('subscription.maxReached', 'Osiągnąłeś maksymalną liczbę pozycji (10).')}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-brand-gold/40 bg-brand-gold/10 p-6 text-center">
            <p className="font-bold text-brand-blue mb-2">
              {t('gifts.comingSoonTitle', 'Prezenty — już wkrótce')}
            </p>
            <p className="text-gray-700 text-sm">
              {t('gifts.comingSoonBody', 'Możliwość zakupu subskrypcji w prezencie pojawi się niebawem. / Gift subscriptions are coming soon.')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
